import React, { useState, useRef, useEffect } from 'react';
import { Menu, Plus, Pencil, Trash2, CornerDownRight, Search, CheckCircle, XCircle } from 'lucide-react';
import { CategoriesController } from './categories/categories.controller';
import { CategoryItem } from './categories/categories.types';
import { useApp } from '../context/AppContext';

export default function CategoriesView() {
  const { currentUser } = useApp();
  const isAdm = currentUser?.role === 'ADM';
  const canAddCategoria = isAdm || currentUser?.permissions?.add?.categoria === true;
  const canEditCategoria = isAdm || currentUser?.permissions?.edit?.categoria === true;
  const canDeleteCategoria = isAdm || currentUser?.permissions?.delete?.categoria === true;

  // Local list retrieved from Controller (React-state managed purely for rendering lists, as instructed)
  const [categoriesList, setCategoriesList] = useState<CategoryItem[]>(() => CategoriesController.list());
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // Responsive screen detection for robust PWA layout adjustments
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileScreen(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // UI toggle states & editing states
  const [editingCategory, setEditingCategory] = useState<CategoryItem | null>(null);
  const [parentSelection, setParentSelection] = useState<string>('main'); // "main" or parent ID
  
  // Toast notifications for success / error alerts
  const [alertState, setAlertState] = useState<{ type: 'success' | 'danger'; msg: string } | null>(null);

  // Reference to the form to handle reset / uncontrolled data reading
  const formRef = useRef<HTMLFormElement>(null);

  // Sync category list whenever query changes
  useEffect(() => {
    let active = true;
    async function load() {
      const list = await CategoriesController.listFromServer(searchQuery);
      if (active) {
        setCategoriesList(list);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [searchQuery]);

  // Helper to trigger alert notifications
  const triggerAlert = (type: 'success' | 'danger', msg: string) => {
    setAlertState({ type, msg });
    setTimeout(() => {
      setAlertState(null);
    }, 5000);
  };

  // Submits the standard form containing raw input data using native FormData
  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    let result;
    if (editingCategory) {
      result = await CategoriesController.handleUpdateOnServer(editingCategory.id, formData);
      if (result.success) {
        triggerAlert('success', `Categoria "${formData.get('name')}" atualizada com sucesso!`);
        setEditingCategory(null);
        setParentSelection('main');
        form.reset();
      } else {
        triggerAlert('danger', result.error || 'Erro ao atualizar a categoria.');
      }
    } else {
      result = await CategoriesController.handleCreateToServer(formData);
      if (result.success) {
        triggerAlert('success', `Categoria "${formData.get('name')}" cadastrada com sucesso!`);
        setParentSelection('main');
        form.reset();
      } else {
        triggerAlert('danger', result.error || 'Erro ao cadastrar categoria.');
      }
    }

    // Refresh state list
    const updatedList = await CategoriesController.listFromServer(searchQuery);
    setCategoriesList(updatedList);
  };

  // Deletes category
  const handleDeleteCategory = async (id: number, name: string) => {
    if (window.confirm(`Tem certeza que deseja excluir a categoria "${name}"?`)) {
      const result = await CategoriesController.handleDeleteOnServer(id);
      if (result.success) {
        triggerAlert('success', `Categoria "${name}" excluída com sucesso!`);
        const updatedList = await CategoriesController.listFromServer(searchQuery);
        setCategoriesList(updatedList);
        if (editingCategory?.id === id) {
          setEditingCategory(null);
        }
      } else {
        triggerAlert('danger', result.error || 'Não foi possível excluir.');
      }
    }
  };

  // Triggers editing phase for a category
  const handleEditTrigger = (cat: CategoryItem) => {
    setEditingCategory(cat);
    setParentSelection(cat.parentId === null ? 'main' : String(cat.parentId));
    
    // Focus the text input for convenience
    const nameInput = formRef.current?.querySelector('input[name="name"]') as HTMLInputElement;
    if (nameInput) {
      nameInput.focus();
    }
    
    // Scroll smoothly to form
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Add subcategory shortcut
  const handleAddSubcategoryShortcut = (parent: CategoryItem) => {
    const check = CategoriesController.canAcceptSubcategory(parent.id);
    if (!check.allowed) {
      triggerAlert('danger', check.reason || 'Esta categoria não pode receber subcategorias.');
      return;
    }

    // Prepare form for subcategory creation directly under this parent
    setEditingCategory(null);
    setParentSelection(String(parent.id));
    
    const nameInput = formRef.current?.querySelector('input[name="name"]') as HTMLInputElement;
    if (nameInput) {
      nameInput.value = '';
      nameInput.focus();
    }
    
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
    triggerAlert('success', `Preenchendo subcategoria direta para "${parent.name}". Digite o nome.`);
  };

  // Resets the state of the form completely
  const handleResetForm = () => {
    setEditingCategory(null);
    setParentSelection('main');
    if (formRef.current) {
      formRef.current.reset();
    }
    triggerAlert('success', 'Formulário limpo com sucesso.');
  };

  // Get list of main categories to represent parent options
  // (Only main categories that don't violate the hierarchy limit)
  const parentsList = categoriesList.filter(
    c => c.parentId === null && (editingCategory ? c.id !== editingCategory.id : true)
  );

  return (
    <div className="fade-in container-fluid py-3 px-1 px-sm-3" id="categories-page">
      
      {/* Header Breadcrumb and global state buttons */}
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 border-bottom pb-3 gap-2">
        <div className="font-display">
          <span className="text-muted fw-normal d-none d-sm-inline">Produtos &gt; Lista de produtos &gt; </span>
          <span className="text-muted fw-normal d-inline d-sm-none">Produtos &gt; </span>
          <span className="text-dark fw-bold">Categorias</span>
        </div>
        {editingCategory && (
          <div className="d-flex align-items-center gap-2 w-100 w-sm-auto">
            <button 
              type="button" 
              className="btn btn-outline-dark rounded font-display px-3 px-sm-4 py-2 flex-grow-1 flex-sm-grow-0" 
              style={{ fontSize: '0.9rem', borderColor: '#ccc', color: '#555' }}
              onClick={handleResetForm}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      {/* Real-time system notifications / feedback */}
      {alertState && (
        <div className={`alert alert-${alertState.type} d-flex align-items-center gap-2 py-2.5 px-3 border-0 transition-all rounded shadow-sm mb-4`} role="alert">
          {alertState.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
          <div className="font-sans text-sm font-medium">{alertState.msg}</div>
        </div>
      )}

      {/* FORM: Categories builder card */}
      {(canAddCategoria || (editingCategory && canEditCategoria)) && (
        <form 
          ref={formRef} 
          onSubmit={handleFormSubmit}
          key={editingCategory ? `edit-${editingCategory.id}` : 'new-form'}
          className="card border shadow-sm p-3 p-sm-4 mb-4 rounded-3 bg-white"
          style={{ borderColor: '#e4e7eb' }}
        >
          <div className="row g-3 mb-3">
            {/* Level selection */}
            <div className="col-12">
              <label className="form-label text-muted text-uppercase font-mono fw-bold mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.05em' }}>
                Selecione o nível da categoria
              </label>
              <select 
                name="parentId"
                value={parentSelection}
                onChange={(e) => setParentSelection(e.target.value)}
                className="form-select form-select-lg text-dark fw-normal bg-light-subtle rounded-3 border"
                style={{ fontSize: '0.95rem', height: '44px' }}
              >
                <option value="main">Mostrar como categoria principal</option>
                {parentsList.map(parent => (
                  <option key={parent.id} value={parent.id}>Subcategoria de: {parent.name}</option>
                ))}
              </select>
            </div>

            {/* Category naming */}
            <div className="col-12">
              <label className="form-label text-muted text-uppercase font-mono fw-bold mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.05em' }}>
                Nome da categoria
              </label>
              <input 
                type="text"
                name="name"
                defaultValue={editingCategory ? editingCategory.name : ''}
                className="form-control form-control-lg text-dark bg-white rounded-3 border"
                placeholder="Digite o nome..."
                style={{ fontSize: '0.95rem', height: '44px' }}
                required
              />
            </div>
          </div>

          {/* Submit Form button aligned right */}
          <div className="d-flex justify-content-end">
            <button 
              type="submit" 
              className="btn btn-primary d-flex align-items-center justify-content-center gap-2 border-0 px-4 py-2.5 font-display rounded-3 text-white w-100 w-sm-auto"
              style={{ backgroundColor: '#4361ee', fontSize: '0.9rem' }}
            >
              <Plus size={18} />
              {editingCategory ? 'Gravar Alterações' : 'Adicionar nova'}
            </button>
          </div>
        </form>
      )}

      {/* Search filter input */}
      <div className="mb-4">
        <div className="input-group input-group-lg border rounded-3 bg-white overflow-hidden shadow-sm">
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control border-0 font-sans text-dark placeholder-muted" 
            placeholder="Buscar..."
            style={{ fontSize: '1rem', height: '48px' }}
          />
          <span className="input-group-text bg-white border-0 text-muted px-3">
            <Search size={20} />
          </span>
        </div>
      </div>

      {/* Grid listing list */}
      <div className="d-flex flex-column gap-2 mb-5">
        {categoriesList.length === 0 ? (
          <div className="text-center py-5 border rounded bg-white text-muted">
            Nenhuma categoria encontrada para "{searchQuery}".
          </div>
        ) : (
          categoriesList.map((cat, idx) => {
            const isChild = cat.parentId !== null;
            return (
              <div 
                key={cat.id} 
                className={`card border shadow-sm p-2.5 p-sm-3 rounded-2 bg-white transition-all`}
                style={{ 
                  marginLeft: isChild ? (isMobileScreen ? '1rem' : '3rem') : '0', 
                  borderColor: editingCategory?.id === cat.id ? '#4361ee' : '#eaedf0',
                  borderLeft: editingCategory?.id === cat.id ? '5px solid #4361ee' : (isChild ? '4px solid #adb5bd' : '1px solid #eaedf0')
                }}
              >
                <div className="d-flex align-items-center justify-content-between gap-1">
                  {/* Left part */}
                  <div className="d-flex align-items-center gap-2 gap-sm-3 min-w-0">
                    {/* Grab handle illustration / list item decoration */}
                    <div className="text-muted d-flex align-items-center flex-shrink-0">
                      <Menu size={16} className="text-secondary opacity-75" />
                    </div>
 
                    {/* Hierarchy layout icon indicator for subcategories */}
                    {isChild && (
                      <div className="text-secondary d-flex align-items-center flex-shrink-0">
                        <CornerDownRight size={14} className="text-muted" />
                      </div>
                    )}
 
                    {/* Sequence Index Code Code string */}
                    <span className="font-mono text-muted text-xxs fw-bold flex-shrink-0" style={{ minWidth: '20px' }}>
                      {cat.code}
                    </span>
 
                    {/* Category Label Name */}
                    <span className={`font-sans text-xs text-sm-md text-dark text-truncate ${isChild ? 'fw-normal text-muted' : 'fw-bold'}`}>
                      {cat.name}
                    </span>
                  </div>
 
                  {/* Actions utility column */}
                  <div className="d-flex align-items-center gap-1.5 flex-shrink-0">
                    
                    {/* Add Subcategory Plus link icon (only allowed for main categories under rules) */}
                    {canAddCategoria && !isChild && (
                      <button 
                        type="button"
                        onClick={() => handleAddSubcategoryShortcut(cat)}
                        title="Adicionar subcategoria rápida" 
                        className="btn btn-sm btn-link text-secondary p-1 rounded hover-bg-light"
                      >
                        <Plus size={18} className="text-dark" strokeWidth={2.5} />
                      </button>
                    )}

                    {/* Edit pen link */}
                    {canEditCategoria && (
                      <button 
                        type="button" 
                        onClick={() => handleEditTrigger(cat)}
                        title="Editar categoria"
                        className="btn btn-sm btn-link text-secondary p-1 rounded hover-bg-light"
                      >
                        <Pencil size={18} className="text-dark" />
                      </button>
                    )}

                    {/* Delete trash link */}
                    {canDeleteCategoria && (
                      <button 
                        type="button" 
                        onClick={() => handleDeleteCategory(cat.id, cat.name)}
                        title="Excluir categoria"
                        className="btn btn-sm btn-link text-link text-danger p-1 rounded hover-bg-light"
                      >
                        <Trash2 size={18} className="text-danger" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

    </div>
  );
}
